/**
 * Backend error codes for Operating Currencies / Operating Countries / Operating
 * Languages and their user-facing messages.
 *
 * The OperatingCurrency/OperatingCountry/OperatingLanguage services raise these
 * as HTTP 400 responses shaped like `{ code: "<CODE>", ... }` (read from
 * `err.response.data.code`). Source of truth:
 *   - apps/backend/common/services/operating_currency_service.py
 *   - apps/backend/common/services/operating_language_service.py (Rule 11)
 */

export const OPERATING_ERROR_CODES = {
  CURRENCY_ALREADY_ADDED: "CURRENCY_ALREADY_ADDED",
  CURRENCY_NOT_AVAILABLE: "CURRENCY_NOT_AVAILABLE",
  CURRENCY_REFERENCE_CANNOT_BE_RETIRED: "CURRENCY_REFERENCE_CANNOT_BE_RETIRED",
  COUNTRY_ALREADY_ADDED: "COUNTRY_ALREADY_ADDED",
  LANGUAGE_ALREADY_ADDED: "LANGUAGE_ALREADY_ADDED",
  LANGUAGE_NOT_AVAILABLE: "LANGUAGE_NOT_AVAILABLE",
  LANGUAGE_SOURCE_CANNOT_BE_ADDED: "LANGUAGE_SOURCE_CANNOT_BE_ADDED",
};

/** Human-readable copy shown inline for each backend error code. */
export const OPERATING_ERROR_MESSAGES = {
  [OPERATING_ERROR_CODES.CURRENCY_ALREADY_ADDED]:
    "Currency already added in operating currencies",
  [OPERATING_ERROR_CODES.CURRENCY_NOT_AVAILABLE]:
    "This currency is not available and cannot be added",
  [OPERATING_ERROR_CODES.CURRENCY_REFERENCE_CANNOT_BE_RETIRED]:
    "The reference currency cannot be retired",
  [OPERATING_ERROR_CODES.COUNTRY_ALREADY_ADDED]:
    "Country already added in operating countries",
  [OPERATING_ERROR_CODES.LANGUAGE_ALREADY_ADDED]:
    "Language already added in operating languages",
  [OPERATING_ERROR_CODES.LANGUAGE_NOT_AVAILABLE]:
    "This language is not available and cannot be added",
  [OPERATING_ERROR_CODES.LANGUAGE_SOURCE_CANNOT_BE_ADDED]:
    "English is the source language and cannot be added as an operating language",
};

/** Fallback message when a code is missing or unrecognised. */
export const DEFAULT_OPERATING_ERROR_MESSAGE =
  "Something went wrong. Please try again.";

/**
 * Resolve a backend error code to its user-facing message.
 * @param {string} code backend error code (e.g. from err.response.data.code)
 * @param {string} [fallback] message used when the code is unknown
 * @returns {string}
 */
export function getOperatingErrorMessage(
  code,
  fallback = DEFAULT_OPERATING_ERROR_MESSAGE
) {
  return OPERATING_ERROR_MESSAGES[code] || fallback;
}
